import React from 'react'
import i18next from 'i18next'
import { Flex } from '../../FlexBox'
import useAudienceQuery, { AudienceType } from './hooks/useAudienceQuery'
import useFeatureFlag from '../../../hooks/useFeatureFlag'
import Dropdown from '../Dropdown'
// @ts-ignore
import AddAudienceIcon from '../../../images/add-audience.svg'
import Placeholder from '../Placeholder'
interface Props {
  onSelect: (value: string[]) => void
  value: string[] | never[]
  onBlur: (e: any) => void
  hasError: boolean
  style?: any
  placeholder?: string
  audience?: AudienceType
}

const GROUP_PREFIX = 'audience-group:'
const isGroupToken = (v: string): boolean =>
  typeof v === 'string' && v.startsWith(GROUP_PREFIX)

const AudienceMultiselect = ({
  placeholder = i18next.t('forms:audience'),
  audience = AudienceType.COMMUNITIES,
  style,
  value,
  onSelect,
  ...rest
}: Props) => {
  const isCommunityAudienceGroupsEnabled = useFeatureFlag(
    'communityAudienceGroupsForMi6'
  )
  const includeAudienceGroups =
    audience === AudienceType.COMMUNITIES && isCommunityAudienceGroupsEnabled
  const { data } = useAudienceQuery(
    audience,
    audience === AudienceType.GROUPS,
    includeAudienceGroups
  )
  const options = data || []
  const realValue: string[] = value || []

  const groupOptions = options.filter((option: any) => option.custom?.isGroup)

  // A group is considered "fully selected" only when every one of its
  // member communities is present in the real (assigned) value.
  const fullySelectedGroups = groupOptions.filter(
    (group: any) =>
      group.custom?.communityIds?.length > 0 &&
      group.custom.communityIds.every((id: string) => realValue.includes(id))
  )

  const autoFilledIds = new Set<string>(
    fullySelectedGroups.flatMap((group: any) => group.custom.communityIds)
  )

  // The visual value shown in the dropdown includes both the group chip(s)
  // (for fully selected groups) AND the real community ids, so the user can
  // remove either the whole group or a single community.
  const displayValue: string[] = [
    ...fullySelectedGroups.map((group: any) => group.value),
    ...realValue
  ]

  const optionsForDisplay = options.map((option: any) => {
    if (option.custom?.isGroup || !autoFilledIds.has(option.value)) {
      return option
    }
    return {
      ...option,
      custom: {
        ...option.custom,
        autoFilled: true
      }
    }
  })

  const handleSelect = (selected: string[]) => {
    const selectedArr = selected || []
    const removedItems = displayValue.filter(v => !selectedArr.includes(v))
    const addedItems = selectedArr.filter(v => !displayValue.includes(v))

    const newReal = new Set<string>(selectedArr.filter(v => !isGroupToken(v)))

    // Deselecting a group chip removes all of its member communities,
    // unless another still-selected group also covers that community.
    removedItems.filter(isGroupToken).forEach(groupToken => {
      const group = groupOptions.find((g: any) => g.value === groupToken)
      if (!group?.custom?.communityIds) return
      group.custom.communityIds.forEach((id: string) => {
        const stillCovered = selectedArr.filter(isGroupToken).some(gt => {
          if (gt === groupToken) return false
          const other = groupOptions.find((g: any) => g.value === gt)
          return other?.custom?.communityIds?.includes(id)
        })
        if (!stillCovered) newReal.delete(id)
      })
    })

    // Selecting a group chip expands it to all of its member communities.
    addedItems.filter(isGroupToken).forEach(groupToken => {
      const group = groupOptions.find((g: any) => g.value === groupToken)
      if (!group?.custom?.communityIds) return
      group.custom.communityIds.forEach((id: string) => newReal.add(id))
    })

    onSelect(Array.from(newReal))
  }

  return (
    <Flex flexDirection="column" style={style}>
      <Placeholder>{placeholder}</Placeholder>
      <Dropdown
        options={optionsForDisplay}
        value={displayValue}
        onSelect={handleSelect}
        isMulti
        placeholder={''}
        closeMenuOnSelect={false}
        dropdownIcon={<AddAudienceIcon />}
        style={{
          backgroundColor: 'transparent',
          marginRight: 10
        }}
        {...rest}
      />
    </Flex>
  )
}

export default AudienceMultiselect
